import { Bot, Globe, Lock, Loader2, Check, Download, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const AutomateTab = ({
  websiteUrl, setWebsiteUrl,
  loginEmail, setLoginEmail,
  loginPassword, setLoginPassword,
  automating, automationStep, automationResult,
  onRunAutomation,
  credits, AUTOMATION_COST
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2 space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600">
            <Bot className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Automate My Website</h2>
            <p className="text-gray-500 text-sm">AI analyzes your site and creates ready-to-post social ads</p>
          </div>
          <div className="ml-auto px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 text-sm font-medium">
            {AUTOMATION_COST} credits
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-gray-700 dark:text-gray-300 font-medium">Website URL</Label>
            <div className="relative mt-2">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://yourstore.com"
                className="pl-10 bg-gray-50 dark:bg-gray-700 border-gray-200"
                data-testid="website-url-input"
              />
            </div>
          </div>

          <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200">
            <Label className="text-gray-700 dark:text-gray-300 font-medium flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Demo Login (Optional)
            </Label>
            <p className="text-xs text-gray-500 mb-3">Provide login to access protected pages</p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="demo@example.com"
                className="bg-white border-gray-200"
                data-testid="login-email-input"
              />
              <Input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Password"
                className="bg-white border-gray-200"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {automating && (
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="font-medium text-blue-800">Processing...</span>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5, 6, 7].map((step) => (
                  <div
                    key={step}
                    className={`h-2 flex-1 rounded-full transition-colors ${
                      step <= automationStep ? "bg-blue-500" : "bg-blue-200"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={onRunAutomation}
            disabled={automating || !websiteUrl.trim() || credits < AUTOMATION_COST}
            className="w-full py-6 text-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 shadow-lg"
            data-testid="start-automation-btn"
          >
            {automating ? (
              <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Analyzing Website...</>
            ) : (
              <><Bot className="h-5 w-5 mr-2" />Start Automation ({AUTOMATION_COST} credits)</>
            )}
          </Button>
        </div>
      </div>

      {automationResult && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
          <h3 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <Check className="h-5 w-5 text-green-500" />
            Generated Social Media Posts
          </h3>
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-600 mb-3">Website Screenshots</p>
            <div className="grid grid-cols-5 gap-3">
              {automationResult.screenshots?.map((screenshot, idx) => (
                <div key={idx} className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative group">
                  <img src={screenshot.image_url} alt={screenshot.caption} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs text-center px-2">{screenshot.caption}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            {automationResult.posts?.map((post, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200">
                <div className="flex gap-4">
                  <div className="w-32 h-32 bg-slate-200 rounded-lg overflow-hidden shrink-0">
                    <img src={post.image_url} alt={post.caption} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 dark:text-white mb-2">{post.caption}</p>
                    <p className="text-sm text-gray-500 mb-3">{post.hashtags}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-xs">
                        <Download className="h-3 w-3 mr-1" />Download
                      </Button>
                      <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700">
                        <Share2 className="h-3 w-3 mr-1" />Post Now
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    <div className="space-y-6">
      <div className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-2xl p-6 text-white">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Bot className="h-5 w-5" />How It Works
        </h3>
        <div className="space-y-4">
          {[
            { step: 1, title: "Enter Your Website", desc: "Provide URL and optional login" },
            { step: 2, title: "AI Analyzes", desc: "Records key flows & takes screenshots" },
            { step: 3, title: "Get Ready Posts", desc: "3 social ads with images & captions" },
          ].map(({ step, title, desc }) => (
            <div key={step} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 text-sm font-bold">{step}</div>
              <div>
                <p className="font-medium">{title}</p>
                <p className="text-sm text-white/70">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-4">What You'll Get</h3>
        <ul className="space-y-3 text-sm text-gray-600">
          {["3-5 Website Screenshots", "3 Ready-to-Post Social Ads", "AI-Generated Captions", "Relevant Hashtags", "One-Click Posting"].map((item) => (
            <li key={item} className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />{item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);
